import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  listAllVisagists,
  deleteVisagist,
  setVisagistPublished,
  setVisagistFoundingMember,
  setVisagistMaxGallery,
  type AdminVisagist,
} from "@/lib/admin-api";
import { toast } from "sonner";
import { SETTING_VISAGIST_MAX_GALLERY, getSettingInt, setSettingInt } from "@/lib/app-settings";
import { GalleryLimitControl } from "@/components/GalleryLimitControl";

export const Route = createFileRoute("/admin/visagists")({
  component: VisagistsAdminPage,
});

function VisagistsAdminPage() {
  const [items, setItems] = useState<AdminVisagist[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [maxGallery, setMaxGallery] = useState<number>(10);
  const [maxGalleryDraft, setMaxGalleryDraft] = useState<string>("10");
  const [savingMax, setSavingMax] = useState(false);
  const [maxMsg, setMaxMsg] = useState<string | null>(null);

  useEffect(() => {
    getSettingInt(SETTING_VISAGIST_MAX_GALLERY, 10).then((v) => {
      setMaxGallery(v); setMaxGalleryDraft(String(v));
    });
  }, []);

  async function saveMaxGallery() {
    const n = parseInt(maxGalleryDraft, 10);
    if (!Number.isFinite(n) || n < 1 || n > 50) {
      setMaxMsg("Bitte einen Wert zwischen 1 und 50 angeben.");
      return;
    }
    setSavingMax(true); setMaxMsg(null);
    try {
      await setSettingInt(SETTING_VISAGIST_MAX_GALLERY, n);
      setMaxGallery(n);
      setMaxMsg("Gespeichert.");
    } catch (e) {
      setMaxMsg(e instanceof Error ? e.message : "Fehler beim Speichern.");
    } finally {
      setSavingMax(false);
    }
  }

  async function load() {
    setLoading(true);
    try { setItems(await listAllVisagists()); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const target = items.find((i) => i.id === confirmId) ?? null;

  async function onDelete(id: string) {
    setBusyId(id);
    try {
      await deleteVisagist(id);
      toast.success("Visagist:in gelöscht");
      setConfirmId(null);
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Löschen fehlgeschlagen");
    } finally {
      setBusyId(null);
    }
  }

  async function togglePublished(p: AdminVisagist) {
    setBusyId(p.id);
    try {
      await setVisagistPublished(p.id, !p.published);
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Update fehlgeschlagen");
    } finally {
      setBusyId(null);
    }
  }

  async function toggleFounding(p: AdminVisagist) {
    setBusyId(p.id);
    try {
      await setVisagistFoundingMember(p.id, !p.founding_member);
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Update fehlgeschlagen");
    } finally {
      setBusyId(null);
    }
  }

  async function saveRowMax(p: AdminVisagist, raw: string) {
    const trimmed = raw.trim();
    let value: number | null;
    if (trimmed === "") {
      value = null;
    } else {
      const n = parseInt(trimmed, 10);
      if (!Number.isFinite(n) || n < 1 || n > 50) {
        toast.error("Bitte einen Wert zwischen 1 und 50 angeben (oder leer für Standard).");
        return;
      }
      value = n;
    }
    setBusyId(p.id);
    try {
      await setVisagistMaxGallery(p.id, value);
      toast.success("Limit gespeichert");
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Update fehlgeschlagen");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <div className="eyebrow text-muted-foreground">Verwaltung</div>
      <h1 className="serif text-4xl md:text-5xl mt-3">Visagist:innen</h1>

      <div className="mt-8 border border-border p-5 grid gap-3 md:flex md:items-end md:gap-6">
        <div>
          <div className="eyebrow text-muted-foreground">Einstellung</div>
          <div className="serif text-lg mt-1">Maximale Bilder in der Profil-Galerie</div>
          <p className="text-xs text-muted-foreground mt-1 max-w-md">
            Standard: <strong>{maxGallery}</strong>. Gilt für alle Visagist:innen ohne individuelles Limit. Pro Person lässt sich unten ein eigenes Limit festlegen (leer = Standard).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input type="number" min={1} max={50} value={maxGalleryDraft} onChange={(e) => setMaxGalleryDraft(e.target.value)} className="w-24 border border-input bg-background px-3 py-2 text-sm" />
          <button type="button" onClick={saveMaxGallery} disabled={savingMax} className="eyebrow border border-foreground bg-foreground text-background px-4 py-2 disabled:opacity-50">{savingMax ? "Speichert …" : "Speichern"}</button>
        </div>
        {maxMsg && <div className="text-xs text-muted-foreground">{maxMsg}</div>}
      </div>

      <div className="mt-8 border border-border divide-y divide-border">
        {loading && <div className="p-6 text-sm text-muted-foreground">Lädt …</div>}
        {!loading && items.length === 0 && (
          <div className="p-6 text-sm text-muted-foreground">Keine Visagist:innen.</div>
        )}
        {items.map((p) => (
          <div key={p.id} className="p-4 md:p-5 grid grid-cols-12 gap-3 items-center">
            <div className="col-span-12 md:col-span-4">
              <div className="serif text-lg">{p.name}</div>
              <div className="text-xs text-muted-foreground">{p.email ?? ""}</div>
            </div>
            <div className="col-span-6 md:col-span-3 text-sm text-muted-foreground">
              {[p.city, p.country].filter(Boolean).join(", ") || p.region || ""}
            </div>
            <div className="col-span-6 md:col-span-2">
              <span className={`eyebrow px-2 py-1 ${p.published ? "bg-foreground text-background" : "border border-border text-muted-foreground"}`}>
                {p.published ? "live" : "offline"}
              </span>
              {p.founding_member && (
                <span className="eyebrow px-2 py-1 ml-2 border border-foreground/40 text-muted-foreground">founding</span>
              )}
            </div>
            <div className="col-span-12 md:col-span-3 flex flex-wrap gap-2 md:justify-end">
              <GalleryLimitControl
                value={p.max_gallery_images}
                disabled={busyId === p.id}
                onSave={(raw: string) => saveRowMax(p, raw)}
              />
              <Link to="/visagists/$id" params={{ id: p.slug }} className="eyebrow border border-border px-3 py-1 hover:bg-muted/30">Ansehen</Link>
              <button disabled={busyId === p.id} onClick={() => togglePublished(p)} className="eyebrow border border-border px-3 py-1 hover:bg-muted/30 disabled:opacity-50">
                {p.published ? "Offline" : "Live"}
              </button>
              <button disabled={busyId === p.id} onClick={() => toggleFounding(p)} className="eyebrow border border-border px-3 py-1 hover:bg-muted/30 disabled:opacity-50">
                {p.founding_member ? "Founding −" : "Founding +"}
              </button>
              <button disabled={busyId === p.id} onClick={() => setConfirmId(p.id)} className="eyebrow border border-destructive text-destructive px-3 py-1 hover:bg-destructive/10 disabled:opacity-50">Löschen</button>
            </div>
          </div>
        ))}
      </div>

      {target && (
        <div className="fixed inset-0 z-50 bg-foreground/40 flex items-center justify-center p-4" onClick={() => busyId ? null : setConfirmId(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-background border border-border w-full max-w-md p-6">
            <div className="eyebrow text-muted-foreground">Löschen bestätigen</div>
            <h2 className="serif text-2xl mt-2">{target.name}</h2>
            <p className="text-sm text-muted-foreground mt-3">
              Das Profil wird endgültig entfernt. Die zugehörige Bewerbung bleibt erhalten.
            </p>
            <div className="mt-6 flex flex-wrap gap-2 justify-end">
              <button disabled={!!busyId} onClick={() => setConfirmId(null)} className="eyebrow border border-border px-4 py-2 disabled:opacity-50">Abbrechen</button>
              <button disabled={!!busyId} onClick={() => onDelete(target.id)} className="eyebrow bg-destructive text-destructive-foreground px-4 py-2 disabled:opacity-50">
                {busyId === target.id ? "Lösche …" : "Endgültig löschen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}